import React, { createContext, useContext, useState, useEffect } from "react";
import {
  login as apiLogin,
  getCurrentUser,
  logout as apiLogout,
} from "../services/authService";
import { getProfile } from "../services/profileService";
import { merchantService, MerchantProfile } from "../services/merchantService";

/* -------------------------------------------------------------
   TYPES DE BASE
------------------------------------------------------------- */
export type UserRole =
  | "merchant_admin"
  | "merchant_finance"
  | "merchant_support"
  | "user";

export interface User {
  id: number;
  username: string;
  firstname?: string;
  lastname?: string;
  merchant_code?: string;
  role?: UserRole;
  avatar?: string;
  email?: string;
  name?: string;
  niveau?: number;
  status?: string;
  user_status?: string;
  institution_code?: string;
  institution?: {  
    institution_name?: string;
    institution_code?: string;
  };
}

/* -------------------------------------------------------------
   TYPE DU COMPTE MARCHAND
------------------------------------------------------------- */
export interface MerchantAccount {
  id: string;
  merchant_id: string;
  merchant_code: string;
  merchant_secrete: string;
  institution_code: string;
  status: number;
  created_at: string;
  updated_at: string;
  callback_urls?: {
    success_url?: string;
    failure_url?: string;
    notification_url?: string;
  };
}

/* -------------------------------------------------------------
   INTERFACE DE RÉPONSE DE LOGIN
------------------------------------------------------------- */
export interface LoginResponse {
  access_token: string;
  token_type: string;
  role: UserRole;
  user: {
    id: number;
    username: string;
    niveau: number;
    firstname: string;
    lastname: string;
    email: string;
    status: string;
    user_status: string;
    institution_code: string;
    merchant_code: string;
  };
}

/* -------------------------------------------------------------
   INTERFACE DU CONTEXTE
------------------------------------------------------------- */
interface AuthContextType {
  user: User | null;
  merchantAccount: MerchantAccount | null;
  merchantProfile: MerchantProfile | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  canAccess: (section: string) => boolean;
  regenerateMerchantSecret: () => Promise<string>;
  updateCallbackUrls: (urls: {
    success_url: string;
    failure_url: string;
    notification_url: string;
  }) => Promise<boolean>;
  loadMerchantProfile: () => Promise<void>;
  loading: boolean;
  profileLoading: boolean;
  token: string | null;
}

/* -------------------------------------------------------------
   CRÉATION DU CONTEXTE
------------------------------------------------------------- */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* -------------------------------------------------------------
   FOURNISSEUR DU CONTEXTE
------------------------------------------------------------- */
export const AuthProvider: React.FC<{ children: React.ReactNode; mockUser?: User }> = ({
  children,
  mockUser,
}) => {
  const [user, setUser] = useState<User | null>(mockUser || null);
  const [merchantAccount, setMerchantAccount] = useState<MerchantAccount | null>(null);
  const [merchantProfile, setMerchantProfile] = useState<MerchantProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!mockUser);
  const [loading, setLoading] = useState(!mockUser);
  const [profileLoading, setProfileLoading] = useState(false);
  const [token, setToken] = useState<string | null>(mockUser ? "mock-token" : null);

  /* -------------------------------------------------------------
     Fonction pour mapper le niveau au rôle
  ------------------------------------------------------------- */
const mapNiveauToRole = (niveau: any): UserRole => {
  // Convertir en nombre si c'est une chaîne
  let niveauNum: number;
  
  if (typeof niveau === 'number') {
    niveauNum = niveau;
  } else if (typeof niveau === 'string') {
    niveauNum = parseInt(niveau, 10);
    if (isNaN(niveauNum)) niveauNum = 0;
  } else {
    niveauNum = 0;
  }
  
  console.log(`🔢 mapNiveauToRole: ${niveau} (${typeof niveau}) => ${niveauNum}`);
  
  switch(niveauNum) {
    case 0: return "merchant_admin";
    case 1: return "merchant_finance";
    case 2: return "merchant_support";
    default: return "user";
  }
};

  /* -------------------------------------------------------------
     Charger le profil marchand
  ------------------------------------------------------------- */
  const loadMerchantProfile = async (): Promise<void> => {
    if (!user?.merchant_code) return;
    try {
      setProfileLoading(true);
      const profile = await merchantService.getMerchantProfile();
      setMerchantProfile(profile);
      setMerchantAccount(profile.merchant);
    } catch (error) {
      console.warn("⚠️ Impossible de charger le profil marchand:", error);
    } finally {
      setProfileLoading(false);
    }
  };

  /* -------------------------------------------------------------
     Charger les données de l'utilisateur depuis le token
  ------------------------------------------------------------- */

const loadUserFromToken = async (token: string): Promise<void> => {
  try {
    // Décode le token JWT pour obtenir les infos de base
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // CORRECTION IMPORTANTE : Convertir niveau en nombre s'il est une chaîne
    let niveau = payload.niveau;
    if (typeof niveau === 'string') {
      niveau = parseInt(niveau, 10);
      if (isNaN(niveau)) niveau = 0;
    }
    
    // Fonction de mapping corrigée
    const mapNiveauToRole = (niveau: number | undefined): UserRole => {
      // CORRECTION : Vérifier si niveau est undefined ou null
      if (niveau === undefined || niveau === null) return "user";
      
      if (niveau === 0) return "merchant_admin";
      if (niveau === 1) return "merchant_finance";
      if (niveau === 2) return "merchant_support";
      return "user";
    };
    
    // Calculer le rôle AVANT de créer l'objet user
    const role = mapNiveauToRole(niveau);
    
    const userData: User = {
      id: payload.id,
      username: payload.sub || payload.username,
      firstname: payload.firstname,
      lastname: payload.lastname,
      email: payload.email,
      merchant_code: payload.merchant_code,
      institution_code: payload.institution_code,
      niveau: niveau, // Utiliser la valeur convertie
      status: payload.status,
      user_status: payload.user_status,
      role: role, // Utiliser le rôle calculé
      name: `${payload.firstname || ''} ${payload.lastname || ''}`.trim(),
    };

    console.log("✅ Utilisateur chargé depuis token:", {
      username: userData.username,
      niveau: userData.niveau,
      role: userData.role,
      niveauType: typeof userData.niveau
    });

    setUser(userData);
    setIsAuthenticated(true);
    
    // Charger les infos complètes depuis l'API
    if (userData.merchant_code) {
      await loadMerchantProfile();
    }
    
  } catch (error) {
    console.error("❌ Erreur lors du décodage du token:", error);
    logout();
  }
};
  /* -------------------------------------------------------------
     🔄 Recharger automatiquement si token (sauf mock)
  ------------------------------------------------------------- */
  // Listen for auth:logout events from API interceptor (token expired)
  useEffect(() => {
    const handleForceLogout = () => {
      console.log("Session expired, redirecting to login...");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setMerchantAccount(null);
      setMerchantProfile(null);
      setToken(null);
      setIsAuthenticated(false);
      window.location.href = "/login";
    };
    window.addEventListener("auth:logout", handleForceLogout);
    return () => window.removeEventListener("auth:logout", handleForceLogout);
  }, []);

  useEffect(() => {
    if (mockUser) return;

    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    // Check if token is expired before using it
    if (storedToken) {
      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          console.log("Token expired on load, redirecting to login...");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setLoading(false);
          window.location.href = "/login";
          return;
        }
      } catch (e) {
        // Invalid token
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setLoading(false);
        window.location.href = "/login";
        return;
      }
    }

    if (storedToken) {
      setToken(storedToken);
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser) as User;
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          // Charger le profil marchand si nécessaire
          if (parsedUser.merchant_code) {
            loadMerchantProfile();
          }
        } catch (error) {
          console.warn("⚠️ Erreur lors du parsing de l'utilisateur:", error);
          localStorage.removeItem("user");
        }
      }
      
      // Charger l'utilisateur depuis le token
      loadUserFromToken(storedToken);
    }
    
    setLoading(false);
  }, []);

  /* -------------------------------------------------------------
     🔐 LOGIN - Adapté à la nouvelle réponse du backend
  ------------------------------------------------------------- */
const login = async (username: string, password: string): Promise<boolean> => {
  if (mockUser) return true;
  
  try {
    const result: LoginResponse = await apiLogin(username, password);
    
    if (result?.access_token) {
      // Stocker le token
      localStorage.setItem("token", result.access_token);
      setToken(result.access_token);
      
      // CORRECTION : S'assurer que niveau est un nombre
      let niveau = result.user.niveau;
      if (typeof niveau === 'string') {
        niveau = parseInt(niveau, 10);
      }
      
      // Préparer l'objet utilisateur
      const userData: User = {
        id: result.user.id,
        username: result.user.username,
        firstname: result.user.firstname,
        lastname: result.user.lastname,
        email: result.user.email,
        merchant_code: result.user.merchant_code,
        institution_code: result.user.institution_code,
        niveau: niveau, // Utiliser la valeur convertie
        status: result.user.status,
        user_status: result.user.user_status,
        role: result.role,
        name: `${result.user.firstname} ${result.user.lastname}`.trim(),
      };
      
      console.log("✅ Login réussi - Données utilisateur:", {
        niveauOriginal: result.user.niveau,
        niveauConverti: niveau,
        role: result.role,
        niveauType: typeof niveau
      });
      
      // Mettre à jour l'état
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      setIsAuthenticated(true);
      
      // Charger le profil marchand si nécessaire
      if (userData.merchant_code) {
        await loadMerchantProfile();
      }

      // Start session tracking (fire-and-forget)
      import('../services/authService').then(m => m.startSession()).catch(() => {});

      return true;
    }
    
    return false;
  } catch (error) {
    console.error("❌ Erreur de login:", error);
    return false;
  }
};
  /* -------------------------------------------------------------
     LOGOUT
  ------------------------------------------------------------- */
  const logout = () => {
    if (mockUser) return;

    // End session tracking (fire-and-forget)
    import('../services/authService').then(m => m.endSession("manual")).catch(() => {});

    apiLogout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("session_token");
    setUser(null);
    setMerchantAccount(null);
    setMerchantProfile(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  /* -------------------------------------------------------------
     Permissions et accès
  ------------------------------------------------------------- */
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    const userRole = user.role || "user";
    
    // Permissions par rôle
    const rolePermissions: Record<UserRole, string[]> = {
      merchant_admin: [
        'all', 'dashboard', 'transfers', 'balances', 'reports', 
        'account', 'team', 'tickets', 'settings', 'analytics', 
        'profile', 'docs', 'notifications', 'merchant_manage'
      ],
      merchant_finance: [
        'finance', 'dashboard', 'balances', 'reports', 
        'analytics', 'tickets', 'profile', 'docs'
      ],
      merchant_support: [
        'support', 'dashboard', 'reports', 'tickets', 
        'support', 'profile', 'docs'
      ],
      user: ['dashboard', 'profile', 'docs']
    };
    
    return rolePermissions[userRole].includes(permission);
  };

  const canAccess = (section: string): boolean => {
    if (!user) return false;
    
    const userRole = user.role || "user";
    const roleAccessMap: Record<UserRole, string[]> = {
      merchant_admin: [
        "dashboard", "transfers", "balances", "reports", 
        "account", "team", "tickets", "settings", 
        "analytics", "profile", "docs", "notifications",
        "merchant", "payments"
      ],
      merchant_finance: [
        "dashboard", "balances", "reports", "analytics", 
        "tickets", "profile", "docs", "transfers"
      ],
      merchant_support: [
        "dashboard", "reports", "tickets", "support", 
        "profile", "docs"
      ],
      user: ["dashboard", "profile", "docs"]
    };
    
    return roleAccessMap[userRole].includes(section);
  };

  /* -------------------------------------------------------------
     Fonctions pour le marchand
  ------------------------------------------------------------- */
  const regenerateMerchantSecret = async (): Promise<string> => {
    try {
      // Implémentez l'appel API ici
      return "new-secret-key";
    } catch (error) {
      console.error("Erreur lors de la régénération du secret:", error);
      throw error;
    }
  };

  const updateCallbackUrls = async (urls: {
    success_url: string;
    failure_url: string;
    notification_url: string;
  }): Promise<boolean> => {
    try {
      // Implémentez l'appel API ici
      return true;
    } catch (error) {
      console.error("Erreur lors de la mise à jour des URLs:", error);
      return false;
    }
  };

  /* -------------------------------------------------------------
     Valeur du contexte
  ------------------------------------------------------------- */
  const contextValue: AuthContextType = {
    user,
    merchantAccount,
    merchantProfile,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    canAccess,
    regenerateMerchantSecret,
    updateCallbackUrls,
    loadMerchantProfile,
    loading,
    profileLoading,
    token,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/* -------------------------------------------------------------
   Hook personnalisé
------------------------------------------------------------- */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

/* -------------------------------------------------------------
   Hook utilitaire pour le token
------------------------------------------------------------- */
export const useToken = () => {
  const { token } = useAuth();
  return token;
};

/* -------------------------------------------------------------
   Hook utilitaire pour vérifier les permissions
------------------------------------------------------------- */
export const usePermission = (permission: string) => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

/* -------------------------------------------------------------
   Hook utilitaire pour vérifier l'accès aux sections
------------------------------------------------------------- */
export const useSectionAccess = (section: string) => {
  const { canAccess } = useAuth();
  return canAccess(section);
};